import { sha256 } from "@noble/hashes/sha2.js";

/**
 * BuildUpload Hook
 *
 * This hook computes the SHA256 hash of a selected build upload file allowing the server to determine if the upload is a duplicate.
 * @noble/hashes is used here to avoid a large memory footprint, since the native crypto.subtle API requires the entire data in memory.
 *
 * Some notes on usage:
 * - This hook currently does not support multiple file uploads at once.
 * - This hook should be on a wrapping div around the upload's <form> element.
 * - The implementing LiveView is expected to implement a `build_upload_requested` handler that receives the SHA256 hash of the upload.
 * - If the upload should proceed, the LiveView should dispatch a `trigger_build_upload` event. Otherwise the upload should be canceled.
 * - Auto upload must be false on the `allow_upload` call because this hook submits the form on `trigger_build_upload`.
 */
export const BuildUpload = {
  mounted() {
    this.handleFileChange = this.handleFileChange.bind(this);
    this.triggerUpload = this.triggerUpload.bind(this);

    if (!this.el.dataset.formId) {
      console.error("BuildUpload: missing data-form-id attribute");
      return;
    }

    this.formSelector = `#${CSS.escape(this.el.dataset.formId)}`;

    this.el.addEventListener("input", this.handleFileChange);

    this.handleEvent("trigger_build_upload", this.triggerUpload);
  },

  destroyed() {
    this.el.removeEventListener("input", this.handleFileChange);
  },

  async handleFileChange(event) {
    const input = event.target;

    if (!(input instanceof HTMLInputElement) || input.type !== "file") return;
    if (!input.closest(this.formSelector)) return;
    if (!(input.files instanceof FileList) || input.files.length === 0) return;

    const file = input.files.item(0);

    try {
      this.pushEventTo(this.el, "build_upload_requested", {
        sha256: await this.calculateSHA256(file),
      });
    } catch (error) {
      console.error("Error calculating SHA256:", error);
      // When calculating SHA256 fails, optimistically trigger the upload
      this.triggerUpload();
    }
  },

  async calculateSHA256(file) {
    const hasher = sha256.create();
    const fileStream = file.stream();
    const reader = fileStream.getReader();

    while (true) {
      const { done, value } = await reader.read();

      if (done) break;

      hasher.update(value);
    }

    const digest = hasher.digest();
    const base16Hash = Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");

    return base16Hash;
  },

  triggerUpload(_event) {
    const form = this.el.querySelector(this.formSelector);

    if (!form) {
      console.error(`BuildUpload: No form found with id '${this.el.dataset.formId}'`);
      return;
    }

    const event = new Event("submit", { bubbles: true, cancelable: true });

    form.dispatchEvent(event);
  },
};
